import type { ShopperAiTagId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  MongoCustomerId,
  MongoUserId,
} from "@redotech/effect-api-shared/ids/mongo-ids";
import type {
  SupportTicketListItemResponse,
  SupportTicketPriority,
  SupportTicketSnoozeOptions,
  SupportTicketStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";

export type MutableSupportTicketListItem = {
  -readonly [K in keyof SupportTicketListItemResponse]: SupportTicketListItemResponse[K];
};

export type SnoozeParams =
  | { duration: Exclude<SupportTicketSnoozeOptions, "custom"> }
  | { duration: "custom"; until: Date }
  | null;

export function computeSnoozeUntil(
  option: Exclude<SupportTicketSnoozeOptions, "custom">,
  now = Date.now(),
): Date {
  switch (option) {
    case "1h":
      return new Date(now + 60 * 60 * 1000);
    case "4h":
      return new Date(now + 4 * 60 * 60 * 1000);
    case "24h":
      return new Date(now + 24 * 60 * 60 * 1000);
  }
}

export function applySupportTicketStatus(
  draft: MutableSupportTicketListItem,
  status: SupportTicketStatus,
): void {
  draft.status = status;
}

export function applySupportTicketReadState(
  draft: MutableSupportTicketListItem,
  readAt: Date | null,
): void {
  draft.readAt = readAt;
}

export function applySupportTicketSnooze(
  draft: MutableSupportTicketListItem,
  option: SnoozeParams,
): void {
  if (option === null) {
    draft.status = "OPEN";
    draft.snoozedUntil = null;
    return;
  }

  draft.status = "SNOOZED";
  draft.snoozedUntil =
    option.duration === "custom"
      ? option.until
      : computeSnoozeUntil(option.duration);
}

export function applySupportTicketPriority(
  draft: MutableSupportTicketListItem,
  priority: SupportTicketPriority | null,
): void {
  draft.priority = priority;
}

export function applySupportTicketAssignee(
  draft: MutableSupportTicketListItem,
  assigneeId: MongoUserId | null,
): void {
  draft.mongoAssigneeId = assigneeId;
}

export function replaceSupportTicketTags(
  draft: MutableSupportTicketListItem,
  tagIds: ShopperAiTagId[],
): void {
  draft.tagIds = tagIds;
}

export function applySupportTicketCustomer(
  draft: MutableSupportTicketListItem,
  mongoCustomerId: MongoCustomerId | null,
): void {
  draft.mongoCustomerId = mongoCustomerId;
  draft.customer = undefined;
}
